import { useState } from 'react';
import { Loader2, Mail, Phone, Plus, RotateCcw, UserRound, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CollapsibleCard } from '@/components/admin/CollapsibleCard';
import {
  CONTACT_ROLE_LABELS,
  useCustomerContacts,
  useSaveContact,
  useSetContactActive,
  type ContactRole,
  type CustomerContact,
} from '@/hooks/admin/useCustomerContacts';

const fieldCls =
  'rounded-lg border border-brix-border bg-brix-surface px-2.5 py-1.5 text-xs text-brix-ink focus:outline-none focus:ring-1 focus:ring-brix-primary';

const ROLE_ORDER: ContactRole[] = [
  'gm',
  'district_manager',
  'vp_ops',
  'facilities',
  'ordering',
  'receiving',
  'ap',
  'other',
];

const BLANK = {
  name: '',
  role: 'gm' as ContactRole,
  title: '',
  email: '',
  phone: '',
};

/**
 * The people at this store — GM, district manager, whoever we call when a
 * delivery has a problem.
 *
 * These are CONTACTS, NOT LOGINS: nothing here grants portal access and
 * nothing here is emailed automatically. Giving someone a login is a
 * separate, deliberate act on the Users tab.
 */
export function StoreContactsCard({ customerId }: { customerId: string }) {
  const [showInactive, setShowInactive] = useState(false);
  const { data, isLoading, error } = useCustomerContacts(customerId, showInactive);
  const saveContact = useSaveContact(customerId);
  const setActive = useSetContactActive(customerId);

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ ...BLANK });
  const [formError, setFormError] = useState<string | null>(null);

  const contacts = data?.contacts ?? [];
  const ordered = [...contacts].sort(
    (a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) || a.name.localeCompare(b.name),
  );

  const submit = async () => {
    setFormError(null);
    if (draft.name.trim() === '') {
      setFormError('Name is required.');
      return;
    }
    try {
      await saveContact.mutateAsync({
        name: draft.name.trim(),
        role: draft.role,
        title: draft.title.trim() || null,
        email: draft.email.trim() || null,
        phone: draft.phone.trim() || null,
      });
      setDraft({ ...BLANK });
      setAdding(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not add that contact.');
    }
  };

  return (
    <CollapsibleCard
      title="Store contacts"
      icon={<UserRound size={14} className="text-brix-muted shrink-0" />}
      storageKey={`contacts-${customerId}`}
      defaultOpen={false}
      summary={ordered.length === 0 ? 'None on file' : `${ordered.length} on file`}
      desc={
        <>
          Who to call about this store. These are contacts, not logins &mdash; nothing here
          grants portal access, and nothing here is emailed automatically.
        </>
      }
      action={
        <>
          <button
            type="button"
            onClick={() => setShowInactive((v) => !v)}
            className="text-[11px] text-brix-muted underline underline-offset-2"
          >
            {showInactive ? 'Hide former' : 'Show former'}
          </button>
          <Button size="sm" variant="secondary" onClick={() => setAdding((v) => !v)}>
            <Plus size={13} className="mr-1" />
            Add
          </Button>
        </>
      }
    >

      {adding ? (
        <div className="rounded-lg border border-brix-border/70 bg-brix-bg/40 px-3 py-3 space-y-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="Name"
              className={`${fieldCls} w-full`}
            />
            <select
              value={draft.role}
              onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value as ContactRole }))}
              className={`${fieldCls} w-full`}
            >
              {ROLE_ORDER.map((r) => (
                <option key={r} value={r}>
                  {CONTACT_ROLE_LABELS[r]}
                </option>
              ))}
            </select>
            <input
              value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              placeholder="Email (optional)"
              className={`${fieldCls} w-full`}
            />
            <input
              value={draft.phone}
              onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
              placeholder="Phone (optional)"
              className={`${fieldCls} w-full`}
            />
          </div>
          {formError ? <p className="text-[12px] text-red-500">{formError}</p> : null}
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => void submit()} disabled={saveContact.isPending}>
              {saveContact.isPending ? <Loader2 size={13} className="animate-spin" /> : 'Save contact'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setFormError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-xs text-brix-muted flex items-center gap-2">
          <Loader2 size={13} className="animate-spin" /> Loading contacts&hellip;
        </p>
      ) : error ? (
        <p className="text-xs text-red-500">
          {error instanceof Error ? error.message : 'Could not load contacts.'}
        </p>
      ) : ordered.length === 0 ? (
        <p className="text-xs text-brix-muted">No contacts on file yet.</p>
      ) : (
        <ul className="divide-y divide-brix-border/60">
          {ordered.map((c) => (
            <ContactRow
              key={c.id}
              contact={c}
              busy={setActive.isPending}
              onSetActive={(active) =>
                void setActive.mutateAsync({ contact_id: c.id, active })
              }
            />
          ))}
        </ul>
      )}
    </CollapsibleCard>
  );
}

function ContactRow({
  contact,
  busy,
  onSetActive,
}: {
  contact: CustomerContact;
  busy: boolean;
  onSetActive: (active: boolean) => void;
}) {
  return (
    <li
      className={`py-2 flex items-start justify-between gap-3 ${contact.active ? '' : 'opacity-55'}`}
    >
      <div className="min-w-0">
        <p className="text-[13px] text-brix-ink font-medium truncate">
          {contact.name}
          {contact.active ? null : (
            <span className="ml-2 text-[10px] uppercase tracking-tight text-brix-muted">
              former
            </span>
          )}
        </p>
        <p className="text-[11px] text-brix-muted">
          {CONTACT_ROLE_LABELS[contact.role]}
          {contact.title ? ` · ${contact.title}` : ''}
        </p>
        <div className="flex items-center gap-3 flex-wrap mt-0.5">
          {contact.email ? (
            <a
              href={`mailto:${contact.email}`}
              className="text-[11px] text-brix-primary flex items-center gap-1 underline underline-offset-2"
            >
              <Mail size={11} /> {contact.email}
            </a>
          ) : null}
          {contact.phone ? (
            <a
              href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}
              className="text-[11px] text-brix-muted flex items-center gap-1"
            >
              <Phone size={11} /> {contact.phone}
            </a>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => onSetActive(!contact.active)}
        title={contact.active ? 'Mark as former contact' : 'Restore contact'}
        className="text-brix-muted hover:text-brix-ink shrink-0 p-1"
      >
        {contact.active ? <X size={14} /> : <RotateCcw size={14} />}
      </button>
    </li>
  );
}

export default StoreContactsCard;
